import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useP } from '../../i18n/useP';
import { getBsView } from '../../api/financials_bs';
import { getPlView, setLineForecast } from '../../api/financials_pl';
import { getForecastAssumptions, putForecastAssumption } from '../../api/forecastAssumptions';
import { PlanCard } from '../../components/PlanCard';
import './InvestAmortPanel.css';

/* Investissements et amortissements — 3 cards, réparties en DEUX ÉTAPES
   (2026-09-19 ; gabarit Planification, maquette validée le 2026-08-10) :

     1. Investissements   — ce qu'on ACHÈTE : libellé, durée de vie en années,
                            date d'acquisition, montant. Une liste, pas une
                            grille mensuelle : un investissement tombe à une
                            date, il ne s'étale pas.
     2. Actifs            — ce qu'il en reste au bilan : valeur de départ,
                            cessions (saisies), résiduel, nouveaux actifs nets,
                            total → bilan
        Amortissements    — ce qu'ils coûtent au résultat : existants (saisis
                            sur les mois ouverts), puis un rang par
                            investissement, total → compte de résultat

   Les DOUZE MOIS s'affichent, ancrés au 31.12 de l'an passé : les mois clos
   portent le réel (valeur au bilan, amortissement au compte de résultat) et ne
   se saisissent pas. Un ancrage au dernier mois clos tomberait au milieu des
   colonnes.

   Durée de vie plutôt que taux : c'est le langage du métier (« ce serveur, je
   l'amortis sur 3 ans »). Le serveur en dérive le taux (`financials_planning`).

   Aucun calcul ici : la ventilation de l'amortissement par investissement vient
   du serveur (`plan_amort_by_line`). La refaire à l'écran dupliquerait la
   formule, donc produirait deux chiffres pour le même actif. */

const MONTHS = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin',
  'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const monthsArr = (o) => Array.from({ length: 12 }, (_, i) => num(o?.[String(i + 1)]));

/* `only` — la section ne montre qu'une partie du panneau (2026-09-19) :
   'capex' = la LISTE des investissements · 'amort' = ce qu'ils deviennent, les
   actifs et leur amortissement. Sans `only`, les trois cartes, comme le monte
   encore le Bilan prévisionnel. Même patron que `HrPlanningHub` : un seul
   composant, plusieurs portées. */
export default function InvestAmortPanel({ activeTenant, year, addToast, unit = 'kchf', only = null }) {
  const show = (k) => !only || only === k;
  const p = useP();
  const tid = activeTenant?.id;
  const [bs, setBs] = useState(null);
  const [pl, setPl] = useState(null);
  const [assum, setAssum] = useState({});

  const load = useCallback(() => {
    if (!tid || !year) { setBs(null); setPl(null); return; }
    getBsView(tid, year, { flatForecast: true }).then((r) => setBs(r.error ? null : r.data)).catch(() => setBs(null));
    getPlView(tid, year).then((r) => setPl(r.error ? null : r.data)).catch(() => setPl(null));
    getForecastAssumptions(tid, year).then((r) => { if (!r.error) setAssum(r.data || {}); }).catch(() => {});
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  const saveAssum = useCallback((key, value) => {
    setAssum((a) => ({ ...a, [key]: value }));            // optimiste
    putForecastAssumption(tid, year, key, value).then(load).catch(() => {});
  }, [tid, year, load]);

  const closed = Number.isFinite(bs?.closed_count) ? bs.closed_count : 0;
  /* LES DOUZE MOIS (2026-09-19) : la table n'affichait que les mois OUVERTS,
     si bien qu'en juillet elle s'ouvrait sur août et que le parc des sept mois
     écoulés restait invisible. Le bilan porte le réel de ces mois-là : on les
     montre, en lecture, et le total redevient celui de l'exercice. */
  const cols = useMemo(() => Array.from({ length: 12 }, (_, i) => i), []);
  const isClosed = (i) => i < closed;
  // Filet vertical au premier mois prévisionnel, comme au compte de résultat.
  const edge = (i) => (closed > 0 && closed < 12 && i === closed ? ' iap-edge' : '');
  const k = unit === 'chf' ? 1 : 1000;
  const fmt = (v) => {
    const x = num(v) / k;
    if (Math.abs(x) < 0.005) return '—';
    return x.toLocaleString('de-CH', { maximumFractionDigits: unit === 'chf' ? 0 : 1 });
  };

  // Durée de vie affichée : celle saisie, ou son équivalent quand la ligne
  // porte encore un taux (lignes créées avant le 2026-08-10). Sans ça, le champ
  // paraît vide alors que l'amortissement se calcule bel et bien.
  const lifeOf = (l) => {
    if (l.life) return l.life;
    const r = Number(l.rate) || 0;
    return r > 0 ? Math.round((100 / r) * 10) / 10 : '';
  };

  const rowByKey = (key) => (bs?.rows || []).find((r) => r.std_key === key) || null;
  const cessLines = Array.isArray(assum.bs_cessions) ? assum.bs_cessions : [];
  const capexLines = Array.isArray(assum.bs_capex) ? assum.bs_capex : [];

  if (!bs) return <p className="iap-empty">{p('Chargement…')}</p>;

  // ── Bloc 1 — actifs existants ────────────────────────────────────────────
  const existRow = rowByKey('exist_asset');
  const cessRow = rowByKey('plan_cessions');            // cumul, négatif
  /* ANCRAGE AU 31.12 PRÉCÉDENT (2026-09-19) : la table partant de janvier, le
     point de départ ne peut plus être le dernier mois clos, qui tomberait au
     MILIEU des colonnes. Les mois écoulés portent le réel du bilan, donc rien
     n'est perdu de ce qui s'est amorti depuis. */
  const openNet = num(existRow?.prev_dec);
  const anchorLabel = `31.12.${year - 1}`;
  const existAmortCum = monthsArr(bs.exist_amort_cum);
  /* Le cumul du bilan ne court que sur les mois PRÉVISIONNELS : sur un mois
     clos, l'amortissement se lit au compte de résultat, où il est comptabilisé.
     Tout y est « existant », rien de nouveau n'ayant pu être planifié dans le
     passé. */
  const depSec = (pl?.rows || []).find((r) => r.code === 'sec:depreciation') || null;
  const amortMonth = (i) => (isClosed(i)
    ? Math.abs(num(depSec?.months?.[String(i + 1)]))
    : existAmortCum[i] - (i > 0 ? existAmortCum[i - 1] : 0));
  // Résiduel = actif existant net (déjà amorti) + cessions (négatives).
  const residual = (i) => num(existRow?.months?.[String(i + 1)]) + num(cessRow?.months?.[String(i + 1)]);
  const cessTotal = cols.reduce((s, i) => s + Math.abs(num(cessLines.reduce(
    (a, l) => a + num(l.vals?.[String(i + 1)]), 0))), 0);
  const amortExistTotal = cols.reduce((s, i) => s + amortMonth(i), 0);
  const lastResidual = cols.length ? residual(cols[cols.length - 1]) : openNet;
  const extinct = openNet > 0 ? Math.max(0, Math.min(100, (1 - lastResidual / openNet) * 100)) : 0;

  // ── Blocs 2 et 3 — nouveaux investissements et leur amortissement ────────
  const newRow = rowByKey('plan_new_asset');
  const amortByLine = bs.plan_amort_by_line || {};
  const newAmortMonth = (i) => Object.values(amortByLine)
    .reduce((s, mm) => s + num(mm?.[String(i + 1)]), 0);

  // ── Bloc 4 — total ───────────────────────────────────────────────────────
  const totalMonth = (i) => amortMonth(i) + newAmortMonth(i);
  const depSection = (pl?.rows || []).find((r) => r.code === 'sec:depreciation');
  const depLines = (pl?.rows || []).filter((r) => r.type === 'detail'
    && r.section === 'depreciation'
    // La ligne synthétique des nouveaux actifs est CALCULÉE (bloc 3) : la
    // proposer à la saisie inviterait à écrire par-dessus un résultat.
    && r.line_role !== 'planned_amort');

  // Ligne du compte de résultat qui porte l'amortissement des actifs existants.
  // On vise le rôle d'ancrage ; à défaut la première ligne réelle de la section
  // (trois clients ont fusionné « Amortissements » et « Corrections de valeur »).
  const amortLine = depLines.find((r) => r.line_role === 'amortization') || depLines[0] || null;
  const saveAmort = (i, raw) => {
    if (!amortLine) return;
    const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
    setLineForecast(tid, { year, reporting_line_id: amortLine.code, month: i + 1,
      amount: Number.isFinite(n) ? -Math.abs(n) * k : 0 }).then(load);
  };

  /* UNE LIGNE D'INVESTISSEMENT = UNE DATE ET UN MONTANT (2026-09-19). Elle se
     saisissait sur douze colonnes mensuelles, comme un flux : mais un
     investissement TOMBE, il ne s'étale pas. Deux champs suffisent, le mois et
     le montant ; l'ANNÉE est celle de l'exercice ouvert, affichée à côté sans
     se taper. Le stockage ne bouge pas (`vals` par mois) : c'est ce que le
     moteur d'amortissement lit, et une ligne saisie avant aujourd'hui garde
     ses valeurs. */
  const acqMonth = (l) => {
    const m = Object.keys(l.vals || {}).find((key) => Math.abs(num(l.vals[key])) > 0.005);
    return m ? Number(m) : null;
  };
  const acqAmount = (l) => {
    const m = acqMonth(l);
    return m ? num(l.vals[String(m)]) : 0;
  };

  const ops = (key, lines) => ({
    add: () => saveAssum(key, [...lines, { id: `p${Date.now()}`, label: '', type: key === 'bs_cessions' ? 'out' : 'in', life: 0, vals: {} }]),
    del: (id) => saveAssum(key, lines.filter((l) => l.id !== id)),
    label: (id, label) => saveAssum(key, lines.map((l) => (l.id === id ? { ...l, label } : l))),
    // Durée de vie en ANNÉES : le serveur en dérive le taux. `rate` reste lu
    // pour les lignes saisies avant le 2026-08-10.
    life: (id, life) => saveAssum(key, lines.map((l) => (l.id === id ? { ...l, life: Number(life) || 0 } : l))),
    /* Le MOIS déplace le montant déjà posé : une ligne n'a qu'une date, sans
       quoi le total de la ligne et sa date diraient deux choses. */
    acq: (id, m) => saveAssum(key, lines.map((l) => {
      if (l.id !== id) return l;
      const amount = acqAmount(l);
      return { ...l, vals: m ? { [String(m)]: amount } : {} };
    })),
    amount: (id, raw, m) => saveAssum(key, lines.map((l) => {
      if (l.id !== id) return l;
      const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
      const month = m || acqMonth(l) || (closed < 12 ? closed + 1 : 12);
      if (!Number.isFinite(n) || Math.abs(n) < 0.005) return { ...l, vals: {} };
      return { ...l, vals: { [String(month)]: Math.abs(n) * k } };
    })),
    val: (id, m, raw) => saveAssum(key, lines.map((l) => {
      if (l.id !== id) return l;
      const n = parseFloat(String(raw).replace(/[^0-9.-]/g, ''));
      const vals = { ...(l.vals || {}) };
      if (!Number.isFinite(n)) delete vals[String(m)];
      else vals[String(m)] = Math.abs(n) * k * (key === 'bs_cessions' ? -1 : 1);
      return { ...l, vals };
    })),
  });

  // `first` absent → pas de colonne d'ancrage (card 3 : une synthèse n'a pas de
  // point de départ). Les cellules `iap-open` des lignes suivent la même règle.
  const Head = ({ head, first, last }) => (
    <thead>
      <tr>
        <th className="iap-lbl">{head}</th>
        {first !== undefined && <th className="iap-open">{first}</th>}
        {cols.map((i) => <th key={i} className={`${isClosed(i) ? 'iap-real' : ''}${edge(i)}`}>{MONTHS[i]}</th>)}
        <th className="iap-end">{last}</th>
      </tr>
    </thead>
  );
  const Anchor = ({ children }) => <td className="iap-open">{children}</td>;


  const editCell = (val, onSave, neg) => (
    <input
      className={`iap-cell${neg ? ' iap-neg' : ''}`} inputMode="decimal" placeholder="—"
      defaultValue={val
        ? (neg ? '−' : '') + Number((Math.abs(val) / k).toFixed(k === 1 ? 0 : 1))
        : ''}
      onBlur={(e) => onSave(e.target.value)} />
  );

  /* ── 1 · Investissements — la LISTE de ce qu'on achète ─────────────────── */
  const cardCapex = (
    <PlanCard key="capex" title={p('Investissements')}>
      <div className="iap-scroll">
        <table className="iap-list">
          <thead>
            <tr>
              <th className="iap-lbl">{p('Libellé')}</th>
              <th className="iap-life">{p('Durée de vie (ans)')}</th>
              <th className="iap-acq">{p('Acquisition')}</th>
              <th className="iap-end">{p('Montant')}</th>
            </tr>
          </thead>
          <tbody>
            {capexLines.map((l) => (
              <tr key={l.id}>
                <td className="iap-lbl">
                  <input className="iap-name" value={l.label || ''} placeholder={p('Libellé')}
                    onChange={(e) => ops('bs_capex', capexLines).label(l.id, e.target.value)} />
                  <button type="button" className="iap-x"
                    onClick={() => ops('bs_capex', capexLines).del(l.id)} aria-label={p('Supprimer')}>×</button>
                </td>
                <td className="iap-life">
                  <input className="iap-rate" inputMode="decimal" value={lifeOf(l)}
                    aria-label={p('Durée de vie (ans)')}
                    onChange={(e) => ops('bs_capex', capexLines).life(l.id, e.target.value)} />
                </td>
                <td className="iap-acq">
                  <select className="iap-month" value={acqMonth(l) || ''}
                          aria-label={p('Mois d\'acquisition')}
                          onChange={(e) => ops('bs_capex', capexLines).acq(l.id, Number(e.target.value) || null)}>
                    <option value="">—</option>
                    {MONTHS.map((mo, i) => (
                      <option key={mo} value={i + 1} disabled={i < closed}>{mo}</option>
                    ))}
                  </select>
                  <span className="iap-yr">{year}</span>
                </td>
                <td className="iap-end">
                  {editCell(acqAmount(l), (v) => ops('bs_capex', capexLines).amount(l.id, v))}
                </td>
              </tr>
            ))}
            <tr className="iap-add">
              <td className="iap-lbl" colSpan={4}>
                <button type="button" className="iap-addbtn"
                  onClick={() => ops('bs_capex', capexLines).add()}>＋ {p('Nouvel investissement')}</button>
              </td>
            </tr>
            <tr className="iap-tot">
              <td className="iap-lbl">{p('Total investissements')}</td>
              <td className="iap-life" />
              <td className="iap-acq" />
              <td className="iap-end">
                {fmt(capexLines.reduce((a, l) => a + acqAmount(l), 0))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="iap-foot">
        <span className="iap-foott">
          {p('L\'année suit l\'exercice ouvert ; un mois clos ne se choisit pas, le passé ne se replanifie pas.')}
        </span>
      </div>
    </PlanCard>
  );

  /* ── 2a · Actifs — ce que les investissements laissent au bilan ────────── */
  const cardAssets = (
    <PlanCard key="assets" title={p('Actifs')}>
      <div className="iap-scroll">
        <table>
          <Head first={anchorLabel} last={p('Total')} />
          <tbody>
            <tr>
              <td className="iap-lbl">{p('Valeur nette au départ')}
                <span className="iap-src">{p('réel au bilan')}</span></td>
              <td className="iap-open iap-v">{fmt(openNet)}</td>
              {cols.map((i) => <td key={i} className={`iap-mut${edge(i)}`}>—</td>)}
              <td className="iap-end" />
            </tr>
            {cessLines.map((l) => (
              <tr key={l.id}>
                <td className="iap-lbl">
                  <input className="iap-name" value={l.label || ''} placeholder={p('Cession')}
                    onChange={(e) => ops('bs_cessions', cessLines).label(l.id, e.target.value)} />
                  <button type="button" className="iap-x"
                    onClick={() => ops('bs_cessions', cessLines).del(l.id)} aria-label={p('Supprimer')}>×</button>
                </td>
                <td className="iap-open" />
                {cols.map((i) => (
                  <td key={i} className={edge(i).trim()}>
                    {isClosed(i)
                      ? <span className="iap-mut">—</span>
                      : editCell(num(l.vals?.[String(i + 1)]),
                        (v) => ops('bs_cessions', cessLines).val(l.id, i + 1, v), true)}
                  </td>
                ))}
                <td className="iap-end iap-neg">
                  {fmt(-Math.abs(cols.reduce((a, i) => a + num(l.vals?.[String(i + 1)]), 0)))}
                </td>
              </tr>
            ))}
            <tr className="iap-add">
              <td className="iap-lbl" colSpan={cols.length + 3}>
                <button type="button" className="iap-addbtn"
                  onClick={() => ops('bs_cessions', cessLines).add()}>＋ {p('Cession')}</button>
              </td>
            </tr>
            <tr>
              <td className="iap-lbl">{p('Valeur nette résiduelle')}
                <span className="iap-src">{p('existants, amortis')}</span></td>
              <td className="iap-open iap-v">{fmt(openNet)}</td>
              {cols.map((i) => <td key={i} className={`iap-v${isClosed(i) ? ' iap-mut' : ''}${edge(i)}`}>{fmt(residual(i))}</td>)}
              <td className="iap-end">{fmt(lastResidual)}</td>
            </tr>
            <tr className="iap-calc">
              <td className="iap-lbl">{p('Nouveaux actifs')}
                <span className="iap-src">{p('nets d\'amortissement')}</span></td>
              <td className="iap-open" />
              {cols.map((i) => <td key={i} className={`iap-v${edge(i)}`}>{fmt(newRow?.months?.[String(i + 1)])}</td>)}
              <td className="iap-end">{fmt(newRow?.months?.[String(12)])}</td>
            </tr>
            <tr className="iap-tot iap-big">
              <td className="iap-lbl">{p('Total actifs')}
                <span className="iap-to">→ {p('bilan')}</span></td>
              <td className="iap-open iap-v">{fmt(openNet)}</td>
              {cols.map((i) => (
                <td key={i} className={`iap-v${isClosed(i) ? ' iap-mut' : ''}${edge(i)}`}>
                  {fmt(residual(i) + num(newRow?.months?.[String(i + 1)]))}
                </td>
              ))}
              <td className="iap-end">
                {fmt(lastResidual + num(newRow?.months?.[String(12)]))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="iap-foot">
        <span className="iap-foott">{p('Départ au')} {anchorLabel} · {closed > 0 ? `${MONTHS[0]}–${MONTHS[closed - 1]} ${p('au réel')}` : p('exercice entier prévisionnel')}</span>
        {lastResidual < -0.005 && (
          <span className="iap-warn">{p('Cessions supérieures à la valeur nette')}</span>
        )}
      </div>
    </PlanCard>
  );

  /* ── 2b · Amortissements — ce qu'ils coûtent au résultat ───────────────── */
  const cardAmort = (
    <PlanCard key="amort" title={p('Amortissements')}>
      <div className="iap-scroll">
        <table>
          <Head first="" last={p('Total')} />
          <tbody>
            <tr>
              <td className="iap-lbl">{p('Amortissement des existants')}
                <span className="iap-src">{amortLine ? amortLine.label : p('aucune ligne')}</span></td>
              <td className="iap-open" />
              {cols.map((i) => (
                <td key={i} className={edge(i).trim()}>
                  {isClosed(i)
                    ? <span className="iap-v iap-mut">{fmt(-amortMonth(i))}</span>
                    : editCell(amortMonth(i), (v) => saveAmort(i, v), true)}
                </td>
              ))}
              <td className="iap-end iap-neg">{fmt(-amortExistTotal)}</td>
            </tr>
            {capexLines.map((l) => {
              const mm = amortByLine[l.id] || {};
              return (
                <tr key={l.id} className="iap-calc">
                  <td className="iap-lbl">{l.label || p('Sans libellé')}
                    {lifeOf(l) ? ` · ${p('linéaire')} ${lifeOf(l)} ${p('ans')}` : ''}</td>
                  <td className="iap-open" />
                  {cols.map((i) => <td key={i} className={`iap-v${isClosed(i) ? ' iap-mut' : ''}${edge(i)}`}>{fmt(mm[String(i + 1)])}</td>)}
                  <td className="iap-end">{fmt(cols.reduce((a, i) => a + num(mm[String(i + 1)]), 0))}</td>
                </tr>
              );
            })}
            <tr className="iap-tot iap-big">
              <td className="iap-lbl">{p('Total amortissements')}
                <span className="iap-to">→ {p('compte de résultat')}</span></td>
              <td className="iap-open" />
              {cols.map((i) => <td key={i} className={`iap-v${isClosed(i) ? ' iap-mut' : ''}${edge(i)}`}>{fmt(totalMonth(i))}</td>)}
              <td className="iap-end">{fmt(cols.reduce((a, i) => a + totalMonth(i), 0))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </PlanCard>
  );

  /* L'ORDRE : ce qu'on ACHÈTE, puis ce que cela DEVIENT (2026-09-19). La
     synthèse qui ouvrait l'écran a disparu : ses deux totaux sont retournés
     dans la carte qu'ils ferment, « Total actifs » avec les actifs et « Total
     amortissements » avec les amortissements. Un résumé qui répète les lignes
     de ses voisines se lit deux fois et se met à jour une fois de trop. */
  return (
    <div className="iap">
      {show('capex') && cardCapex}
      {show('amort') && cardAssets}
      {show('amort') && cardAmort}
    </div>
  );
}
